// Session 27: the agency's course view (/admin/courses). Agency only.
//
// Three things Leena asked for, in one place:
//   1. per client: the switch, seats used, and who has done what
//   2. the time-saved numbers, which are the before-and-after for the upsell
//   3. every "What's missing?" answer in one list, newest first. A complex ask
//      in that list is an AI-optimisation lead, which is the point of the box
//
// Reads v_portal_course_seats (agency only), v_portal_course_progress and
// v_portal_course_responses. The views do the scoping; this page does no
// filtering of its own beyond grouping for display.
//
// Tabular data is CSS-grid rows of divs, the same shape as .results-table.
// The portal has no <table> anywhere and this page does not introduce one.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';
import { COURSES, courseByKey, readyLessons } from '../courses/index.js';

const MODE_LABEL = { on: 'On', preview: 'Preview', off: 'Off' };
const MODE_CLASS = { on: 'chip-run-ok', preview: 'chip-run-wait', off: 'chip-run-muted' };

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export default function AdminCourses() {
  const { isAgency } = useAuth();
  const [seats, setSeats] = useState([]);
  const [progress, setProgress] = useState([]);
  const [responses, setResponses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [s, p, r] = await Promise.all([
      supabase.from('v_portal_course_seats').select('*').order('client_name'),
      supabase.from('v_portal_course_progress').select('*'),
      supabase.from('v_portal_course_responses').select('*').order('updated_at', { ascending: false }),
    ]);
    const firstErr = s.error || p.error || r.error;
    if (firstErr) { setError(firstErr.message); setLoading(false); return; }
    setSeats(s.data || []);
    setProgress(p.data || []);
    setResponses(r.data || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const missing = useMemo(
    () => responses.filter((r) => r.kind === 'whats_missing' && r.payload?.text),
    [responses]
  );
  const estimates = useMemo(
    () => responses.filter((r) => r.kind === 'time_estimate'),
    [responses]
  );

  if (!isAgency) return <Navigate to="/" replace />;

  return (
    <>
      <h1>Courses</h1>
      <p className="lede">
        Who is taking what, the time they say they saved, and everything they told us was missing.
      </p>
      {error && <p className="banner banner-err">{error}</p>}
      {loading && <p className="lede">Loading…</p>}

      {!loading && (
        <>
          <section className="course-block">
            <h2>Per client</h2>
            <div className="card course-grid course-grid-seats">
              <div className="course-grid-row course-grid-head">
                <span>Client</span><span>Courses</span><span>People</span>
                <span>Started</span><span>Seats</span>
              </div>
              {seats.map((s) => (
                <div key={s.client_id} className="course-grid-row">
                  <span className="course-grid-name">{s.client_name}</span>
                  <span><em className={`chip ${MODE_CLASS[s.mode] || ''}`}>{MODE_LABEL[s.mode] || s.mode}</em></span>
                  <span className="course-num">{s.people}</span>
                  <span className="course-num">{s.people_started}</span>
                  <span className="course-num">
                    {s.people} of {s.seat_cap}{s.people > s.seat_cap ? ' (over)' : ''}
                  </span>
                </div>
              ))}
              {seats.length === 0 && (
                <div className="course-grid-row"><span>No clients.</span></div>
              )}
            </div>
          </section>

          <section className="course-block">
            <h2>Who has done what</h2>
            {COURSES.map((course) => (
              <PerCourse key={course.key} course={course} progress={progress} />
            ))}
          </section>

          <section className="course-block">
            <h2>Time saved, as they report it</h2>
            {estimates.length === 0 ? (
              <p className="course-muted">Nobody has answered the time question yet.</p>
            ) : (
              <ul className="course-list">
                {groupEstimates(estimates).map((g) => (
                  <li key={g.user_id}>
                    <strong>{g.display_name || 'Someone'}</strong> ({g.client_id}):{' '}
                    {g.start !== undefined ? `${g.start} h/week before` : 'no starting number'}
                    {g.end !== undefined ? `, ${g.end} after` : ''}
                    {g.saved !== undefined ? ` — they say ${g.saved} h/week saved` : ''}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="course-block">
            <h2>What&rsquo;s missing ({missing.length})</h2>
            <p className="course-muted">
              Every answer, newest first. A complex ask here is a lead, not a complaint.
            </p>
            {missing.length === 0 ? (
              <p className="course-muted">Nothing yet.</p>
            ) : (
              <div className="course-missing-list">
                {missing.map((m) => (
                  <div key={m.id} className="card course-missing-item">
                    <p className="course-missing-text">{m.payload.text}</p>
                    <p className="course-muted">
                      {m.display_name || 'Someone'} · {m.client_id} · {lessonLabel(m)} · {fmtDate(m.updated_at)}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </>
  );
}

function PerCourse({ course, progress }) {
  const ready = readyLessons(course);
  const rows = progress.filter((p) => p.course_key === course.key);
  const people = [...new Map(rows.map((r) => [r.user_id, r])).values()];

  if (ready.length === 0) {
    return (
      <p className="course-muted">
        <strong>{course.title}</strong>: no lessons published yet.
      </p>
    );
  }

  // One column per lesson, sized by how many there are, so the grid stays
  // readable as a course grows.
  const style = { '--course-lesson-cols': ready.length };

  return (
    <div className="course-percourse">
      <h3>{course.title}</h3>
      {people.length === 0 ? (
        <p className="course-muted">Nobody has opened it yet.</p>
      ) : (
        <div className="card course-grid course-grid-people" style={style}>
          <div className="course-grid-row course-grid-head">
            <span>Person</span><span>Client</span>
            {ready.map((l) => <span key={l.key} className="course-num">{l.key}</span>)}
            <span className="course-num">Done</span>
          </div>
          {people.map((person) => {
            const mine = rows.filter((r) => r.user_id === person.user_id);
            const doneSet = new Set(mine.filter((r) => r.status === 'complete').map((r) => r.lesson_key));
            const startedSet = new Set(mine.map((r) => r.lesson_key));
            return (
              <div key={person.user_id} className="course-grid-row">
                <span className="course-grid-name">{person.display_name || 'Unnamed'}</span>
                <span>{person.client_id}</span>
                {ready.map((l) => (
                  <span key={l.key} className="course-num">
                    {doneSet.has(l.key) ? 'done' : startedSet.has(l.key) ? 'started' : '—'}
                  </span>
                ))}
                <span className="course-num">
                  {ready.filter((l) => doneSet.has(l.key)).length} of {ready.length}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// One row per person, folding their start and end answers together.
function groupEstimates(rows) {
  const by = new Map();
  for (const r of rows) {
    const g = by.get(r.user_id) || {
      user_id: r.user_id, display_name: r.display_name, client_id: r.client_id,
    };
    if (r.payload?.when === 'start') g.start = r.payload.hours_per_week;
    if (r.payload?.when === 'end') {
      g.end = r.payload.hours_per_week;
      if (r.payload.hours_saved !== undefined) g.saved = r.payload.hours_saved;
    }
    by.set(r.user_id, g);
  }
  return [...by.values()];
}

function lessonLabel(row) {
  const course = courseByKey(row.course_key);
  const lesson = course?.lessons.find((l) => l.key === row.lesson_key);
  if (!course) return row.course_key;
  return lesson ? `${course.title}, ${lesson.title}` : course.title;
}
